import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Briefcase, User, Download, Check, Truck, Package, Boxes, Shirt, Building2 } from "lucide-react";
import { useI18n } from "../contexts/LanguageContext";
import { API_BASE } from "../lib/api";
import api from "../lib/api";
import Counter from "../components/Counter";
import useSeo from "../hooks/useSeo";

const PARTNERS = [
  { name: "Net-A-Porter", icon: Shirt },
  { name: "Ryder", icon: Truck },
  { name: "Hantover", icon: Boxes },
  { name: "White Toque", icon: Package },
  { name: "ArrowPac", icon: Building2 },
];

export default function Home() {
  const { t, lang } = useI18n();
  const [services, setServices] = useState([]);

  useSeo({
    title: lang === "es" ? "Agencia de Staffing Bilingüe en Union City, NJ" : "Bilingual Staffing Agency in Union City, NJ",
    description: lang === "es"
      ? "ENCES Global - Agencia de personal bilingüe con 16+ años de experiencia. 500+ colocaciones al año en logística, manufactura y administración. Contrata talento en menos de 48 horas."
      : "ENCES Global - Bilingual staffing agency with 16+ years of experience. 500+ placements per year across logistics, manufacturing and administration. Hire talent in under 48 hours.",
    path: "/",
  });

  useEffect(() => { api.get("/services").then((r) => setServices(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="page-home">
      {/* HERO SPLIT */}
      <section className="grid grid-cols-1 md:grid-cols-2 min-h-[88vh] bg-white border-b border-slate-100">
        <div className="flex flex-col justify-center container-pad py-16 md:py-20 animate-fade-up">
          <div className="text-xs tracking-[0.3em] uppercase text-slate-500 mb-6" data-testid="hero-eyebrow">{t.hero.eyebrow}</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-navy leading-[1.05]" data-testid="hero-title">
            {t.hero.title}
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed" data-testid="hero-subtitle">{t.hero.subtitle}</p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <Link to="/hire" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-navy text-white font-semibold hover:bg-navy/90 transition-colors shadow-lg shadow-navy/20" data-testid="hero-cta-business">
              <Briefcase size={18} /> {t.hero.ctaBusiness} <ArrowRight size={16} />
            </Link>
            <Link to="/jobs" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border-2 border-navy text-navy font-semibold hover:bg-navy hover:text-white transition-colors" data-testid="hero-cta-worker">
              <User size={18} /> {t.hero.ctaWorker} <ArrowRight size={16} />
            </Link>
          </div>

          <a href={`${API_BASE}/brochure`} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-2 self-start px-5 py-2.5 rounded-full bg-brandred/10 text-brandred font-semibold text-sm hover:bg-brandred hover:text-white transition-colors" data-testid="hero-download-brochure">
            <Download size={14} /> {t.common.downloadBrochure}
          </a>

          <div className="mt-12 text-xs tracking-widest uppercase text-slate-500" data-testid="hero-trust">{t.hero.trust}</div>
        </div>

        <div className="relative w-full h-[40vh] md:h-auto bg-slate-100" data-testid="hero-image-wrap">
          <img src={`${API_BASE}/hero-image`} alt="ENCES CEO" className="absolute inset-0 w-full h-full object-cover md:rounded-bl-[80px]" />
          <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 bg-white/95 backdrop-blur px-5 py-4 rounded-2xl shadow-xl">
            <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500">CEO & Founder</div>
            <div className="font-bold text-navy">Cesar Medina</div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="bg-soft py-12">
        <div className="container-pad mx-auto max-w-[1400px] grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { v: 16, s: "+", label: t.stats.years },
            { v: 500, s: "+", label: t.stats.placements },
            { v: 200, s: "+", label: t.stats.companies },
            { v: 95, s: "%", label: t.stats.satisfaction },
          ].map((x, i) => (
            <div key={i} data-testid={`stat-${i}`}>
              <div className="text-4xl md:text-5xl font-extrabold text-navy"><Counter to={x.v} suffix={x.s} /></div>
              <div className="text-sm text-slate-600 mt-1">{x.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* MISSION */}
      <section className="section-pad container-pad mx-auto max-w-[1400px] grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div className="relative">
          <img src="https://images.unsplash.com/photo-1606836591695-4d58a73eba1e?auto=format&fit=crop&q=80&w=1400" alt="Team meeting" className="rounded-[24px] shadow-soft w-full h-[440px] object-cover" />
          <div className="absolute -bottom-6 -right-6 bg-brandred text-white rounded-2xl px-6 py-5 shadow-2xl">
            <div className="text-3xl font-extrabold">2009</div>
            <div className="text-[10px] uppercase tracking-[0.25em]">{t.mission.stamp}</div>
          </div>
        </div>
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-brandred mb-3">{t.mission.eyebrow}</div>
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-navy">{t.mission.title}</h2>
          <p className="mt-5 text-slate-600 text-lg leading-relaxed">{t.mission.body}</p>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section-pad bg-soft">
        <div className="container-pad mx-auto max-w-[1400px]">
          <div className="text-xs tracking-[0.3em] uppercase text-brandred mb-3">{t.services.eyebrow}</div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-navy max-w-2xl">{t.services.title}</h2>
            <p className="text-slate-600 max-w-md">{t.services.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.map((s, i) => (
              <div key={s.key} className="bg-white rounded-[24px] p-6 border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all" data-testid={`service-card-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-navy text-white flex items-center justify-center font-bold mb-4">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-bold text-navy mb-2 text-lg">{s[lang]?.name}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s[lang]?.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/services" className="inline-flex items-center gap-2 text-navy font-semibold hover:text-brandred" data-testid="services-view-all">
              {t.services.viewAll} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="section-pad container-pad mx-auto max-w-[1400px] grid grid-cols-1 md:grid-cols-2 gap-16 items-start">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-brandred mb-3">{t.why.eyebrow}</div>
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-navy">{t.why.title}</h2>
        </div>
        <ul className="space-y-4">
          {t.why.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-3" data-testid={`why-bullet-${i}`}>
              <span className="mt-1 w-6 h-6 rounded-full bg-green-100 text-green-700 inline-flex items-center justify-center"><Check size={14} /></span>
              <span className="text-slate-700 text-lg">{b}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* PARTNERS */}
      <section className="bg-soft py-16">
        <div className="container-pad mx-auto max-w-[1400px] text-center">
          <div className="text-xs tracking-[0.3em] uppercase text-brandred mb-3">{t.partners.eyebrow}</div>
          <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-navy mb-10">{t.partners.title}</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {PARTNERS.map(({ name, icon: Icon }) => (
              <div key={name} className="bg-white rounded-2xl py-6 px-4 flex items-center justify-center gap-2 border border-slate-100 hover:border-navy transition-colors" data-testid={`partner-${name.toLowerCase().replace(/[^a-z]/g,"-")}`}>
                <Icon size={20} className="text-navy" />
                <span className="font-bold text-navy">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section-pad container-pad mx-auto max-w-[1400px]">
        <div className="rounded-[32px] bg-navy text-white p-10 md:p-16 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">{t.finalCta.title}</h2>
            <p className="mt-3 text-white/70 text-lg">{t.finalCta.subtitle}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
            <Link to="/hire" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-brandred hover:bg-red-700 text-white font-semibold" data-testid="final-cta-business">{t.finalCta.business} <ArrowRight size={16} /></Link>
            <Link to="/jobs" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white text-navy font-semibold hover:bg-soft" data-testid="final-cta-worker">{t.finalCta.worker} <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
